import { apiClient } from './client';

export interface Asset {
  id: number; asset_number: string; name: string; description: string | null; category_id: number | null; asset_type: string | null;
  manufacturer: string | null; model: string | null; serial_number: string | null; purchase_date: string | null; purchase_cost: string | null;
  purchase_cost_currency: string | null; supplier: string | null; project_id: number | null; programme_id: number | null; donor_id: number | null;
  custodian_employee_id: number | null; condition_status: string; status: string; warranty_start: string | null; warranty_end: string | null;
  notes: string | null; created_at: string;
}

export interface AssetAssignment {
  id: number; assigned_to_type: string; assigned_to_employee_id: number | null; assigned_to_label: string | null; assignment_type: string;
  assignment_date: string; expected_return_date: string | null; actual_return_date: string | null; condition_at_issue: string; condition_at_return: string | null;
}

export interface AssetMovement {
  id: number; origin_label: string; destination_label: string; responsible_employee_id: number; dispatch_date: string; receipt_date: string | null;
  condition_on_dispatch: string; condition_on_receipt: string | null; status: string;
}

export interface AssetDisposal {
  id: number; disposal_reference: string; reason: string; condition_at_disposal: string; book_value: string | null;
  recommended_method: string; recipient_buyer: string | null; proceeds: string | null; status: string; disposal_date: string | null;
}

export const assetsApi = {
  list: (filters: { status?: string; search?: string } = {}) => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][]);
    return apiClient.get<{ data: Asset[] }>(`/api/assets${params.toString() ? `?${params}` : ''}`);
  },
  get: (id: number) => apiClient.get<{ data: Asset }>(`/api/assets/${id}`),
  create: (input: Record<string, unknown>) => apiClient.post<{ data: { id: number; assetNumber: string } }>('/api/assets', input),

  listAssignments: (id: number) => apiClient.get<{ data: AssetAssignment[] }>(`/api/assets/${id}/assignments`),
  assign: (id: number, input: Record<string, unknown>) => apiClient.post<{ data: { id: number } }>(`/api/assets/${id}/assignments`, input),
  returnAssignment: (id: number, assignmentId: number, input: Record<string, unknown>) =>
    apiClient.post<{ data: { assignmentId: number; status: string } }>(`/api/assets/${id}/assignments/${assignmentId}/return`, input),

  listMovements: (id: number) => apiClient.get<{ data: AssetMovement[] }>(`/api/assets/${id}/movements`),
  createMovement: (id: number, input: Record<string, unknown>) => apiClient.post<{ data: { id: number } }>(`/api/assets/${id}/movements`, input),
  receiveMovement: (id: number, movementId: number, input: Record<string, unknown>) =>
    apiClient.post<{ data: { movementId: number; status: string } }>(`/api/assets/${id}/movements/${movementId}/receive`, input),

  getDisposal: (id: number) => apiClient.get<{ data: AssetDisposal }>(`/api/assets/${id}/disposal`),
  createDisposal: (id: number, input: Record<string, unknown>) => apiClient.post<{ data: { id: number; disposalReference: string } }>(`/api/assets/${id}/disposal`, input),
  submitDisposal: (disposalId: number) => apiClient.post<{ data: { id: number; status: string } }>(`/api/asset-disposals/${disposalId}/submit`, {}),
  approveDisposal: (disposalId: number) => apiClient.post<{ data: { id: number; status: string } }>(`/api/asset-disposals/${disposalId}/approve`, {}),
  rejectDisposal: (disposalId: number, reason: string) => apiClient.post<{ data: { id: number; status: string } }>(`/api/asset-disposals/${disposalId}/reject`, { reason }),
  completeDisposal: (disposalId: number, input: Record<string, unknown>) => apiClient.post<{ data: { id: number; status: string } }>(`/api/asset-disposals/${disposalId}/complete`, input),
};
